import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Search, KeyRound, ArrowRight } from "lucide-react";

const BUYERS_BG = "/assets/prefooter-buyers.webp";
const SELLERS_BG = "/assets/prefooter-sellers.webp";

// Encart signature global "Achat / Vente" — composition unique séparée par une diagonale.
export function PreFooterCTA() {
  const { t } = useTranslation();
  const [hover, setHover] = useState(null); // 'buyers' | 'sellers' | null

  // Diagonale dynamique (desktop) : 50/50 -> ~53/47 au survol.
  // Survol : la photo du côté survolé s'ouvre en PLEINE largeur (gauche→droite, droite→gauche).
  const seamTop = hover === "buyers" ? 100 : hover === "sellers" ? 0 : 54;
  const seamBot = hover === "buyers" ? 100 : hover === "sellers" ? 0 : 46;
  const leftClip = `polygon(0 0, ${seamTop}% 0, ${seamBot}% 100%, 0 100%)`;
  const rightClip = `polygon(${seamTop}% 0, 100% 0, 100% 100%, ${seamBot}% 100%)`;
  const clipTransition = "clip-path 0.5s cubic-bezier(0.22,1,0.36,1)";

  const Buyers = ({ align = "left", sfx = "" }) => (
    <div className={`relative z-10 flex flex-col ${align === "left" ? "items-start text-left" : "items-start text-left"} justify-center h-full`}>
      <span className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[#C19A6B] mb-2.5">{t("prefooter.buyers_surtitre")}</span>
      <h2 className="text-2xl sm:text-3xl lg:text-[2.4rem] font-light text-white tracking-tight leading-[1.1] mb-3">{t("prefooter.buyers_title")}</h2>
      <p className="text-sm font-light text-zinc-300 leading-relaxed max-w-sm mb-6">{t("prefooter.buyers_text")}</p>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <Link to="/espace-acquereurs" data-testid={`prefooter-cta-buyers-primary${sfx}`}
          className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-[#050505] font-semibold uppercase tracking-wider text-[11px] sm:text-xs px-6 py-3.5 rounded-[5px] shadow-[0_4px_20px_rgba(193,154,107,0.25)] hover:shadow-[0_6px_28px_rgba(193,154,107,0.4)] hover:-translate-y-0.5 transition-all duration-300">
          <Search className="w-4 h-4" /> {t("prefooter.buyers_cta")}
        </Link>
        <Link to="/biens-off-market" data-testid={`prefooter-cta-buyers-secondary${sfx}`}
          className="group inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-white/80 hover:text-[#e0c79a] transition-colors">
          {t("prefooter.buyers_cta2")} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );

  const Sellers = ({ align = "right", sfx = "" }) => (
    <div className={`relative z-10 flex flex-col ${align === "right" ? "items-end text-right" : "items-start text-left"} justify-center h-full`}>
      <span className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[#C19A6B] mb-2.5">{t("prefooter.sellers_surtitre")}</span>
      <h2 className="text-2xl sm:text-3xl lg:text-[2.4rem] font-light text-white tracking-tight leading-[1.1] mb-3">{t("prefooter.sellers_title")}</h2>
      <p className={`text-sm font-light text-zinc-300 leading-relaxed max-w-sm mb-6 ${align === "right" ? "ml-auto" : ""}`}>{t("prefooter.sellers_text")}</p>
      <div className={`flex flex-wrap items-center gap-x-5 gap-y-3 ${align === "right" ? "justify-end" : ""}`}>
        <Link to="/proposer" data-testid={`prefooter-cta-sellers-primary${sfx}`}
          className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-[#050505] font-semibold uppercase tracking-wider text-[11px] sm:text-xs px-6 py-3.5 rounded-[5px] shadow-[0_4px_20px_rgba(193,154,107,0.25)] hover:shadow-[0_6px_28px_rgba(193,154,107,0.4)] hover:-translate-y-0.5 transition-all duration-300">
          <KeyRound className="w-4 h-4" /> {t("prefooter.sellers_cta")}
        </Link>
        <Link to="/philosophie" data-testid={`prefooter-cta-sellers-secondary${sfx}`}
          className="group inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-white/80 hover:text-[#e0c79a] transition-colors">
          {t("prefooter.sellers_cta2")} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );

  return (
    <section data-testid="prefooter-cta" className="relative bg-[#050505]">
      {/* ===== DESKTOP : composition diagonale ===== */}
      <div
        className="hidden lg:block relative w-full min-h-[380px] overflow-hidden"
        onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHover((e.clientX - r.left) < r.width / 2 ? "buyers" : "sellers"); }}
        onMouseLeave={() => setHover(null)}
      >
        {/* Panneau ACHETEURS (gauche) */}
        <div
          className="absolute inset-0 will-change-[clip-path]"
          style={{ clipPath: leftClip, transition: clipTransition }}
          data-testid="prefooter-buyers-panel"
        >
          <div className="absolute inset-0 bg-no-repeat" style={{ backgroundImage: `url(${BUYERS_BG})`, backgroundSize: "118% auto", backgroundPosition: "6% 28%" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.72) 55%, rgba(5,5,5,0.6) 100%)" }} />
          <Search className="absolute -bottom-6 left-10 w-40 h-40 text-[#C19A6B]/[0.06]" strokeWidth={1} aria-hidden="true" />
          <div className="relative h-full max-w-7xl mx-auto pl-8 xl:pl-14 pr-[46%] py-14 min-h-[380px]">
            <Buyers />
          </div>
        </div>
        {/* Panneau VENDEURS (droite) */}
        <div
          className="absolute inset-0 will-change-[clip-path]"
          style={{ clipPath: rightClip, transition: clipTransition }}
          data-testid="prefooter-sellers-panel"
        >
          <div className="absolute inset-0 bg-no-repeat" style={{ backgroundImage: `url(${SELLERS_BG})`, backgroundSize: "128% auto", backgroundPosition: "2% 32%" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(10,10,12,0.62) 0%, rgba(10,10,12,0.75) 45%, rgba(10,10,12,0.92) 100%)" }} />
          <KeyRound className="absolute -bottom-6 right-10 w-40 h-40 text-[#C19A6B]/[0.06]" strokeWidth={1} aria-hidden="true" />
          <div className="relative h-full max-w-7xl mx-auto pr-8 xl:pr-14 pl-[46%] py-14 min-h-[380px]">
            <Sellers />
          </div>
        </div>
        {/* Liseré doré sur la diagonale */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ clipPath: leftClip, transition: clipTransition, boxShadow: "inset -1px 0 0 rgba(193,154,107,0.35)" }} />
      </div>

      {/* ===== MOBILE : empilé, séparation diagonale légère ===== */}
      <div className="lg:hidden">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5 }}
          className="relative overflow-hidden -mb-4" style={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 22px), 0 100%)" }}>
          <div className="absolute inset-0 bg-cover bg-left" style={{ backgroundImage: `url(${BUYERS_BG})` }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.82), rgba(5,5,5,0.9))" }} />
          <div className="relative px-6 pt-12 pb-16"><Buyers align="left" sfx="-m" /></div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: 0.1 }}
          className="relative overflow-hidden border-t border-[#C19A6B]/30">
          <div className="absolute inset-0 bg-cover bg-right" style={{ backgroundImage: `url(${SELLERS_BG})` }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,12,0.88), rgba(10,10,12,0.94))" }} />
          <div className="relative px-6 pt-14 pb-12"><Sellers align="left" sfx="-m" /></div>
        </motion.div>
      </div>
    </section>
  );
}
